import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  FolderOpen,
  Settings,
  LogOut,
  Sparkles,
  Tag,
  Users,
  Bell,
  Calendar,
  Zap,
  History,
  Sliders,
  Menu,
  DollarSign,
  Image,
  X,
  Crown,
  Shield,
  RefreshCw,
  Mail,
  Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils/cn';

// Version et date de build - BUG-6 fix: centralisé sur Build 304
const BUILD_VERSION = '304';
const BUILD_DATE = '2026-02-27';

const menuItems = [
  // 1. Tableau de bord - Vue d'ensemble
  { name: 'Tableau de bord', path: '/admin', icon: LayoutDashboard, exact: true },

  // 2. Commandes - Le plus utilisé quotidiennement
  { name: 'Commandes', path: '/admin/orders', icon: ShoppingCart },

  // 3. Gestion du Menu - Produits, catégories, stock
  { name: 'Gestion du Menu', path: '/admin/menu', icon: Menu, highlight: true },

  // 4. Promotions V2 - Nouveau moteur de promotions
  { name: 'Promotions', path: '/admin/promotions', icon: Tag, highlight: true },

  // 4.5 Ventes Upsell - Produits suggérés dans le panier
  { name: 'Ventes Upsell', path: '/admin/upsell', icon: DollarSign, highlight: true },

  // 5. Notifications - Communication clients
  { name: 'Notifications', path: '/admin/notifications', icon: Bell },

  { name: 'Email Innovorder', path: '/admin/email-innovorder', icon: Mail, highlight: true },

  // 6. Chiffre d'Affaires - Analytics
  { name: 'Chiffre d\'Affaires', path: '/admin/revenue', icon: DollarSign },

  // 6.5 Favoris synchronisés - Statistiques (accès contrôlé côté serveur)
  { name: 'Favoris', path: '/admin/favorites', icon: Star },

  // 7. IA Marketing - Campagnes intelligentes
  { name: 'IA Marketing', path: '/admin/ai-marketing', icon: Zap, section: true },
  { name: 'Campagnes proposées', path: '/admin/ai-marketing/campaigns', icon: Zap, indent: true },
  { name: 'Historique & Résultats', path: '/admin/ai-marketing/history', icon: History, indent: true },
  { name: 'Paramètres IA', path: '/admin/ai-marketing/settings', icon: Sliders, indent: true },

  // 8. SURPRISE DU JOUR - MODULE INDÉPENDANT (avec onglets internes)
  { name: 'Surprise du Jour', path: '/admin/surprise-du-jour', icon: Sparkles, highlight: true },

  // 8.5 CHRONO 10.00 - Jeu du chronomètre
  { name: 'Chrono 10.00', path: '/admin/chrono-game', icon: Zap, highlight: true },

  // 9. Clients - Gestion clientèle
  { name: 'Clients', path: '/admin/customers', icon: Users },

  // 10. Assistant IA - Aide contextuelle
  { name: 'Assistant IA', path: '/admin/ai', icon: Sparkles },

  // 11.5 Popups - Gestion popups app mobile
  { name: 'Popups', path: '/admin/popups', icon: Image, highlight: true },


  // 12. Paramètres - Configuration
  { name: 'Paramètres', path: '/admin/settings', icon: Settings },
];

export const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const { logout, user, isSuperAdmin, role } = useAuth();

  // Handler pour fermer la sidebar après navigation sur mobile
  const handleNavClick = () => {
    // Fermer uniquement sur mobile (md = 768px)
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <div
      className={cn(
        // Base styles
        "w-64 bg-white border-r border-gray-200 h-screen fixed left-0 top-0 flex flex-col z-50",
        // Mobile: hidden par défaut, slide in quand ouvert
        "transform transition-transform duration-300 ease-in-out",
        "-translate-x-full md:translate-x-0",
        // Quand ouvert sur mobile
        isOpen && "translate-x-0"
      )}
    >
      {/* Header avec bouton fermer sur mobile */}
      <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-primary">Family's</h1>
          <p className="text-xs md:text-sm text-gray-500">Back Office</p>
        </div>
        {/* Bouton fermer - visible uniquement sur mobile */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition-all"
          onClick={onClose}
          aria-label="Fermer le menu"
        >
          <X className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto">
        {/* Lien Gestion des Admins - Super Admin uniquement */}
        {isSuperAdmin && (
          <NavLink
            to="/admin/users"
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center space-x-3 px-3 md:px-4 py-3 rounded-lg transition-all duration-200 min-h-[44px] mb-2',
                'border-2 border-purple-300',
                isActive
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold shadow-lg'
                  : 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 font-bold hover:from-purple-100 hover:to-purple-200'
              )
            }
          >
            <Crown className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm md:text-base">Gestion des Admins</span>
          </NavLink>
        )}

        {menuItems.map((item) => {
          if (item.section) {
            return (
              <div key={item.path} className="pt-4 pb-2">
                <div className="flex items-center space-x-2 px-3 md:px-4 text-sm font-bold text-primary">
                  <item.icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </div>
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  // Base - touch-friendly avec min-height 44px
                  'flex items-center space-x-3 px-3 md:px-4 py-3 rounded-lg transition-all duration-200 min-h-[44px]',
                  item.indent && 'pl-6 md:pl-8',
                  item.highlight && 'border-2 border-orange-300',
                  isActive
                    ? item.highlight
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold shadow-lg'
                      : 'bg-primary text-white font-semibold'
                    : item.highlight
                      ? 'bg-gradient-to-r from-orange-50 to-red-50 text-orange-700 font-bold hover:from-orange-100 hover:to-red-100'
                      : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm md:text-base">{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-3 md:p-4 border-t border-gray-200">
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.email || 'Admin'}</p>
            {role && (
              <span className={cn(
                'text-xs px-2 py-0.5 rounded-full font-medium',
                role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                role === 'admin' ? 'bg-red-100 text-red-700' :
                role === 'manager' ? 'bg-blue-100 text-blue-700' :
                'bg-green-100 text-green-700'
              )}>
                {role === 'super_admin' ? 'Super Admin' :
                 role === 'admin' ? 'Admin' :
                 role === 'manager' ? 'Manager' : 'Livreur'}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">Le Family's</p>
        </div>
        <button
          onClick={() => { logout(); window.location.href = "/select-mode"; }}
          className="flex items-center space-x-2 w-full px-3 md:px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100 transition-all min-h-[44px]"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-semibold">Déconnexion</span>
        </button>
      </div>

      {/* Version & Refresh */}
      <div className="p-2 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <p className="text-xs text-gray-400">Build {BUILD_VERSION}</p>
            <p className="text-[10px] text-gray-300">{BUILD_DATE}</p>
          </div>
          <button
            onClick={() => {
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                  registrations.forEach(registration => registration.unregister());
                });
              }
              caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
              });
              window.location.reload(true);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all"
            title="Rafraîchir l'application"
          >
            <RefreshCw className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
};
